import { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { Layout } from "@/components/layout";
import { SEOHead } from "@/components/seo";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { useToast } from "@/hooks/use-toast";
import { useAutoRegistration } from "@/hooks/useAutoRegistration";
import { supabase } from "@/integrations/supabase/client";
import { trackGA4Event } from "@/lib/ga4";
import { trackPixelLead } from "@/lib/retargeting";
import { generateLocalBusinessSchema } from "@/components/seo/SchemaMarkup";
import { Home, MapPin } from "lucide-react";
import { useAgentBranding } from "@/hooks/useAgentBranding";
import AgentBrandingCard from "@/components/agent/AgentBrandingCard";

const PAGE_TITLE = "What Is My Home Worth? | Free South Jersey Home Valuation | Prime Realty Partners";
const PAGE_DESC = "Find out what your South Jersey home is worth. Get a free instant home valuation from Prime Realty Partners.";
const CANONICAL = "https://www.prpsj.com/home-value";

const webPageSchema = {
  "@context": "https://schema.org",
  "@type": "WebPage",
  name: "What Is My Home Worth?",
  description: PAGE_DESC,
  url: CANONICAL,
};

const localBusinessSchema = generateLocalBusinessSchema();

const communities = [
  { label: "Camden County", towns: [
    { name: "Cherry Hill", slug: "cherry-hill-homes-for-sale" },
    { name: "Haddonfield", slug: "haddonfield-homes-for-sale" },
    { name: "Collingswood", slug: "collingswood-homes-for-sale" },
    { name: "Voorhees", slug: "voorhees-homes-for-sale" },
    { name: "Pennsauken", slug: "pennsauken-homes-for-sale" },
    { name: "Bellmawr", slug: "bellmawr-homes-for-sale" },
  ]},
  { label: "Burlington County", towns: [
    { name: "Moorestown", slug: "moorestown-homes-for-sale" },
    { name: "Mount Laurel", slug: "mount-laurel-homes-for-sale" },
    { name: "Marlton", slug: "marlton-homes-for-sale" },
    { name: "Cinnaminson", slug: "cinnaminson-homes-for-sale" },
    { name: "Delran", slug: "delran-homes-for-sale" },
    { name: "Medford", slug: "medford-homes-for-sale" },
  ]},
  { label: "Gloucester County", towns: [
    { name: "Washington Township", slug: "washington-township-homes-for-sale" },
    { name: "Deptford", slug: "deptford-homes-for-sale" },
    { name: "Woolwich", slug: "woolwich-homes-for-sale" },
  ]},
];

const HomeValue = () => {
  useEffect(() => {
    document.title = PAGE_TITLE;
  }, []);

  const formRef = useRef<HTMLDivElement>(null);
  const { toast } = useToast();
  const { autoRegisterLead } = useAutoRegistration();
  const { brandedAgent, isBranded, agentSlug } = useAgentBranding();
  const [submitting, setSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    name: "", email: "", phone: "", address: "", city: "", zip_code: "", tcpa_consent: false,
  });

  const scrollToForm = () => {
    formRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const { error: dbError } = await supabase.from("seller_leads").insert({
        name: formData.name,
        email: formData.email,
        phone: formData.phone || "",
        address: formData.address,
        city: formData.city || null,
        state: "NJ",
        zip_code: formData.zip_code || null,
        tcpa_consent: formData.tcpa_consent,
        consent_timestamp: formData.tcpa_consent ? new Date().toISOString() : null,
      });
      if (dbError) throw dbError;

      try {
        await supabase.functions.invoke("submit-lead", {
          body: {
            event_type: "home_valuation",
            lead_data: {
              name: formData.name,
              email: formData.email,
              phone: formData.phone || "",
              tcpa_consent: formData.tcpa_consent,
              consent_timestamp: formData.tcpa_consent ? new Date().toISOString() : null,
              source_page: window.location.href,
            },
            listing_data: {
              address: formData.address,
              city: formData.city,
              state: "NJ",
              zip_code: formData.zip_code,
            },
            metadata: {
              ...(agentSlug && { assign_to_agent: true, agent_slug: agentSlug }),
            },
          },
        });
      } catch (fubError) {
        console.log("FUB sync skipped or failed:", fubError);
      }

      toast({ title: "Request Submitted!", description: "We'll contact you shortly with your home valuation." });
      trackPixelLead({ formType: "home_valuation", contentName: "Home Value - Valuation Request" });
      trackGA4Event("form_submit", { event_category: "lead_action", form_name: "home_valuation", form_page: window.location.pathname });
      await autoRegisterLead({ name: formData.name, email: formData.email, phone: formData.phone });
      setFormData({ name: "", email: "", phone: "", address: "", city: "", zip_code: "", tcpa_consent: false });
    } catch {
      toast({ title: "Error", description: "Failed to submit request. Please try again.", variant: "destructive" });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Layout>
      <SEOHead
        title={PAGE_TITLE}
        description={PAGE_DESC}
        canonicalUrl={CANONICAL}
        noindex={isBranded}
        schema={[webPageSchema, localBusinessSchema]}
        keywords="what is my home worth, free home valuation NJ, home value South Jersey, CMA, comparative market analysis NJ"
        breadcrumbs={[{ name: "Home", url: "https://www.prpsj.com" }, { name: "Home Value", url: CANONICAL }]}
      />

      {/* Hero + Form */}
      <section className="relative bg-gradient-to-br from-primary/10 to-primary/5 py-20">
        <div className="container">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h1 className="font-heading text-4xl md:text-5xl font-bold mb-4">What Is My Home Worth?</h1>
              <p className="text-lg text-muted-foreground">
                Get a free, no-obligation home valuation from a local South Jersey expert.
              </p>
            </div>

            {isBranded && brandedAgent && (
              <div className="mt-8">
                <AgentBrandingCard agent={brandedAgent} />
              </div>
            )}

            <div ref={formRef}>
              <Card className="shadow-lg">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Home className="h-5 w-5 text-primary" />
                    Get My Free Home Valuation
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <form onSubmit={handleSubmit} className="space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="name">Full Name *</Label>
                        <Input id="name" value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} required />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="email">Email *</Label>
                        <Input id="email" type="email" value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })} required />
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="phone">Phone</Label>
                      <Input id="phone" type="tel" value={formData.phone} onChange={(e) => setFormData({ ...formData, phone: e.target.value })} />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="address">Property Address *</Label>
                      <Input id="address" value={formData.address} onChange={(e) => setFormData({ ...formData, address: e.target.value })} placeholder="123 Main Street" required />
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="city">City</Label>
                        <Input id="city" value={formData.city} onChange={(e) => setFormData({ ...formData, city: e.target.value })} />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="zip">Zip Code</Label>
                        <Input id="zip" value={formData.zip_code} onChange={(e) => setFormData({ ...formData, zip_code: e.target.value })} />
                      </div>
                    </div>

                    <div className="flex items-start gap-3">
                      <Checkbox id="consent" checked={formData.tcpa_consent} onCheckedChange={(checked) => setFormData({ ...formData, tcpa_consent: checked as boolean })} />
                      <Label htmlFor="consent" className="text-xs text-muted-foreground leading-tight">
                        By submitting this form, you agree to be contacted by MarChris Ventures LLC dba Prime Realty Partners via call, email, and text regarding real estate and mortgage services, including property updates, appointment reminders, and transactional communications. Message frequency may vary. Message and data rates may apply. You may opt out at any time by replying STOP, or reply HELP for assistance.
                        <br />Consent is not a condition of purchase.
                        <br /><a href="/privacy" className="underline hover:text-primary">Privacy Policy</a>
                      </Label>
                    </div>

                    <Button type="submit" size="lg" className="w-full" disabled={submitting}>
                      {submitting ? "Submitting..." : "Get My Free Home Valuation"}
                    </Button>
                  </form>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Content */}
      <article className="py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 space-y-16">

          <section>
            <h2 className="font-heading text-3xl font-bold mb-6">Get Your Free Comparative Market Analysis</h2>
            <p className="text-muted-foreground mb-4">Online home value estimators like Zillow's Zestimate and Redfin's estimates use algorithms to approximate your home's value. While they're a useful starting point, they often miss the nuances that affect home values in South Jersey — things like recent renovations, lot characteristics, neighborhood micro-trends, and school district boundaries.</p>
            <p className="text-muted-foreground">A Comparative Market Analysis (CMA) from a local agent gives you an accurate picture based on recent sales of similar homes in your specific neighborhood. Our CMAs include recently sold comparable properties, current market conditions in your town, adjustments for your home's unique features, and a recommended listing price range if you're considering selling.</p>
          </section>

          <section>
            <h2 className="font-heading text-3xl font-bold mb-6">Why Online Estimators Miss the Mark in South Jersey</h2>
            <p className="text-muted-foreground mb-4">South Jersey's real estate market has characteristics that make automated estimates less reliable. Property values can shift dramatically within a few blocks — a home in one Cherry Hill zip code (08002) can be worth significantly more than a similar home in another (08034). School district boundaries, flood zones, proximity to PATCO stations, and local development all affect values in ways algorithms struggle to capture.</p>
            <p className="text-muted-foreground">That's why a local expert who knows the difference between Kingston and Barclay Farm, or between Old Moorestown and the new developments, provides a more accurate valuation than any website.</p>
          </section>

          <section>
            <h2 className="font-heading text-3xl font-bold mb-6">South Jersey Communities We Serve</h2>
            <p className="text-muted-foreground mb-6">We provide free home valuations across all of South Jersey, including:</p>
            <div className="grid md:grid-cols-3 gap-6">
              {communities.map((county) => (
                <div key={county.label}>
                  <h3 className="font-heading text-xl font-bold mb-3 flex items-center gap-2">
                    <MapPin className="h-5 w-5 text-primary" />
                    {county.label}
                  </h3>
                  <ul className="space-y-1">
                    {county.towns.map((town) => (
                      <li key={town.slug}>
                        <Link to={`/${town.slug}`} className="text-primary hover:underline">{town.name}</Link>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </section>

          {/* Explore Communities */}
          <section>
            <h2 className="font-heading text-3xl font-bold mb-4">Explore South Jersey Communities</h2>
            <p className="text-muted-foreground mb-6">Ready to start your search? Browse homes for sale across South Jersey's most popular communities.</p>
            <div className="flex flex-wrap gap-2">
              {[
                { name: "Cherry Hill", path: "/cherry-hill-homes-for-sale" },
                { name: "Moorestown", path: "/moorestown-homes-for-sale" },
                { name: "Mount Laurel", path: "/mount-laurel-homes-for-sale" },
                { name: "Marlton", path: "/marlton-homes-for-sale" },
                { name: "Voorhees", path: "/voorhees-homes-for-sale" },
                { name: "Haddonfield", path: "/haddonfield-homes-for-sale" },
                { name: "Washington Township", path: "/washington-township-homes-for-sale" },
                { name: "Gloucester Township", path: "/gloucester-township-homes-for-sale" },
                { name: "Deptford", path: "/deptford-homes-for-sale" },
                { name: "Cinnaminson", path: "/cinnaminson-homes-for-sale" },
                { name: "Medford", path: "/medford-homes-for-sale" },
                { name: "Collingswood", path: "/collingswood-homes-for-sale" },
              ].map((c) => (
                <Link key={c.name} to={c.path} className="inline-flex items-center px-3 py-1.5 rounded-full border border-border text-sm text-primary hover:bg-primary/5 transition-colors">
                  {c.name}
                </Link>
              ))}
            </div>
          </section>

          {/* CTA */}
          <section className="bg-gradient-to-br from-primary/10 to-primary/5 rounded-2xl p-8 md:p-12 text-center">
            <h2 className="font-heading text-3xl font-bold mb-4">Ready to Find Out What Your Home Is Worth?</h2>
            <p className="text-muted-foreground mb-6 max-w-2xl mx-auto">
              Our team provides detailed, accurate home valuations at no cost and no obligation. Whether you're thinking about selling now or just curious about your equity, we're here to help.
            </p>
            <Button size="lg" onClick={scrollToForm}>
              Request My Free Valuation
            </Button>
          </section>
        </div>
      </article>
    </Layout>
  );
};

export default HomeValue;
